import { useCallback, useReducer, useRef } from "react";

/**
 * Guards in-flight customer SMS sends, keyed by order + action.
 *
 * The registry lives above the order rows rather than inside them. A card can
 * unmount mid-send (switching List <-> By time, collapsing a time window, a
 * filter that drops the row) and the order data still reads "not notified"
 * until the round trip lands, so a freshly mounted card would otherwise offer
 * the same send again and text the customer twice.
 *
 * The set is a ref, read at tap time, so a second thumb tap dispatched before
 * React can re-render still sees the gate closed; the bump only drives the
 * visual cue. A key is only ever released by the send that took it, so a slow
 * round trip stays guarded for as long as it actually takes.
 */
export function useSendGuard() {
  const inFlightRef = useRef<Set<string>>(new Set());
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const isSending = useCallback((key: string) => inFlightRef.current.has(key), []);

  const runGuarded = useCallback(async (key: string, send: () => void | Promise<void>) => {
    if (inFlightRef.current.has(key)) return;
    inFlightRef.current.add(key);
    bump();
    try {
      await send();
    } catch (error) {
      console.error(`Send failed for ${key}:`, error);
    } finally {
      inFlightRef.current.delete(key);
      bump();
    }
  }, []);

  return { isSending, runGuarded };
}

export default useSendGuard;
